import { useState, useRef, useEffect, useMemo, Fragment } from "react";
import {
  ArrowLeft,
  Mic,
  Send,
  Plus,
  AtSign,
  Sparkles,
  Copy,
  Bookmark,
  ShieldCheck,
  Image as ImageIcon,
  BookOpen,
  FolderOpen,
  ChevronRight,
  FileText,
  Lightbulb,
  Check,
  ClipboardList,
  Layers,
  Scan,
  Library,
  AlertOctagon,
  BrainCircuit,
  X,
  ArrowRight,
  Calendar,
  MapPin,
  Activity,
  Users,
  Quote,
  Tag,
} from "lucide-react";
import Ni2Icon from "@/components/system/Ni2Icon";
import ProvenanceTag from "@/components/system/ProvenanceTag";
import Ni2MentionMenu from "@/components/ni2/Ni2MentionMenu";
import Ni2ScopeSelector from "@/components/ni2/Ni2ScopeSelector";
import Ni2StructuredAnswer, { scopeToBannerLabel } from "@/components/ni2/Ni2StructuredAnswer";
import Ni2MyLibraryPanel from "@/components/ni2/Ni2MyLibraryPanel";
import ProvenanceWorkspacePanel from "@/components/ni2/ProvenanceWorkspacePanel";
import { useNi2Scope } from "@/context/Ni2ScopeContext";
import { useProvenanceWorkspace, classifyProvenanceId } from "@/context/ProvenanceWorkspaceContext";
import MMConfirmationCard from "@/components/mm/MMConfirmationCard";
import MMDepartmentMemoryCard from "@/components/mm/MMDepartmentMemoryCard";
import { matchNi2Intent } from "@/context/Ni2Intents";
import { useLiveMode } from "@/context/LiveModeContext";
import { streamNi2Chat } from "@/data/llmClient";
import { BookOpen as BookIcon } from "lucide-react";
import { useWorkspaceMode } from "@/context/WorkspaceModeContext";

/* Demo conversation, canned response, and provenance-preview shape
   all live in the shared data layer now. Ni² Workspace only wires
   the workbook-backed view models into the existing UI. */
import {
  getNi2DemoConversation,
  getNi2CannedResponse,
  resolvePreviewForChip,
} from "@/data/viewModels";

const ACTIVE_CONVERSATION = getNi2DemoConversation();
const CANNED_RESPONSE     = getNi2CannedResponse();

const SUGGESTIONS = [
  "Prep me for the next vestibular schwannoma case",
  "Compare my last VS operations",
  "Reflections tagged CN VII",
  "M&M candidates this month",
];

/* Citation → sidebar conversation flash. Since Conversations aren't a
   workbook table, this is a small UI-only mapping between real case /
   reflection / imaging refs and the CV-* rows in ConversationsSidebar. */
const CITATION_TO_CONV = {
  "VS-RAO-001": "CV-2143",
  "VS-RAO-002": "CV-2144",
  "VS-RAO-003": "CV-2140",
  "VS-RAO-004": "CV-2145",
  "VS-RAO-005": "CV-2138",
  "VS-RAO-006": "CV-2141",
  "REF-001":    "CV-2145",
  "REF-002":    "CV-2140",
  "IMG-001":    "CV-2143",
  "LIT-001":    "CV-2138",
  "LIT-002":    "CV-2138",
  "MM-001":     "CV-2090",
};

const WORKSPACE_HEIGHT = "calc(100vh - 168px)";

/* Repository-backed provenance-record resolver. Replaces the old
   inline SOURCE_LIBRARY. */
function findSource(refOrChip) {
  if (!refOrChip) return null;
  if (typeof refOrChip === "string") {
    // Try each kind — chips call us with a full {label,ref} object,
    // but a few legacy call-sites pass just the ref string.
    return resolvePreviewForChip({ ref: refOrChip, label: "Case" })
        || resolvePreviewForChip({ ref: refOrChip, label: "Reflection" })
        || resolvePreviewForChip({ ref: refOrChip, label: "Imaging" })
        || resolvePreviewForChip({ ref: refOrChip, label: "Paper" })
        || resolvePreviewForChip({ ref: refOrChip, label: "M&M" });
  }
  return resolvePreviewForChip(refOrChip);
}


/* ------------------ Root ------------------ */
export default function Ni2Workspace({ onBack, onOpenFiles, filesActive, activeImagingId, activeImagingPlaceholder }) {
  const {
    mode: workspaceMode,
    activeConversationId,
    setActiveConversationId,
  } = useWorkspaceMode();
  const empty = activeConversationId === null;

  const [turns, setTurns] = useState(ACTIVE_CONVERSATION.turns);
  useEffect(() => {
    if (!empty) setTurns(ACTIVE_CONVERSATION.turns);
    else setTurns([]);
  }, [empty, activeConversationId]);

  const [animGen, setAnimGen] = useState(0);
  useEffect(() => {
    if (workspaceMode === "ni2" && !empty) setAnimGen((g) => g + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceMode, activeConversationId]);

  const scrollRef = useRef(null);
  const autoScrollRef = useRef(true);

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    if (!autoScrollRef.current && smooth) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  useEffect(() => {
    if (workspaceMode !== "ni2" || empty) return;
    const id = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [workspaceMode, activeConversationId, empty, animGen]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      autoScrollRef.current = distance < 60;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const { liveActive } = useLiveMode();
  const { scope: ni2Scope, setScope: setNi2Scope, displayLabel: ni2ScopeDisplayLabel } = useNi2Scope();
  const {
    active: provActive,
    openProvenance,
    closeProvenance,
    leftPct,
    setLeftPct,
    MIN_LEFT_PCT,
    MIN_RIGHT_PCT,
    isOpen: provOpen,
  } = useProvenanceWorkspace();

  // Narrow viewport → open provenance as a full-screen sheet rather
  // than a split. Threshold picked so a landscape iPad still gets the
  // split, while phones fall back to the sheet.
  const [isNarrow, setIsNarrow] = useState(() => {
    try { return typeof window !== "undefined" ? window.innerWidth < 900 : false; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => {
      if (typeof window === "undefined") return;
      setIsNarrow(window.innerWidth < 900);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close provenance on Escape (unless another overlay owns Escape).
  useEffect(() => {
    if (!provOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeProvenance(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [provOpen, closeProvenance]);

  /* Demo Mode: hides developer tool names in favour of a single
     human-readable retrieval banner. Uses localStorage so the demo
     stays clean across refreshes; a hidden query param
     `?ni2_debug=1` flips into developer mode for engineering. */
  const [demoMode, setDemoModeState] = useState(() => {
    try {
      if (typeof window !== "undefined" && window.location.search.includes("ni2_debug=1")) return false;
      const stored = localStorage.getItem("ni2_demo_mode");
      return stored === null ? true : stored === "1";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try { localStorage.setItem("ni2_demo_mode", demoMode ? "1" : "0"); } catch {}
  }, [demoMode]);

  /* My Library — read-only side panel listing personal records. */
  const [libraryOpen, setLibraryOpen] = useState(() => {
    try { return typeof window !== "undefined" ? window.innerWidth > 1180 : false; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => {
      if (typeof window === "undefined") return;
      if (window.innerWidth <= 1180 && libraryOpen) setLibraryOpen(false);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [libraryOpen]);

  /* Follow-up + inline chip handlers threaded into Ni2LiveBubble. */
  const handleLiveFollowUp = (action) => {
    if (!action) return;
    const target = action.target || "";
    if (target.startsWith("scope:")) {
      setNi2Scope(target.slice("scope:".length));
      return;
    }
    // Any other action re-prompts Ni² with an intent-shaped question so
    // the model naturally re-retrieves against the referenced record.
    const map = {
      "open-reflection": `Open reflection ${target} and show me its full context.`,
      "open-imaging":    `Open imaging ${target} and describe the key findings.`,
      "review-cases":    `Review cases similar to ${target}.`,
      "open-mm":         `Open M&M discussion ${target}.`,
      "open-lit":        `Open paper ${target} and summarise its key findings.`,
      "generate-mm":     `Draft an M&M discussion for ${target}.`,
    };
    const prompt = map[action.id] || `Tell me more about ${target}.`;
    handleSend(prompt);
  };

  const handleLibraryOpenRecord = ({ kind, id }) => {
    const bykind = {
      cases:       `Open case ${id} and show me its surgical memory timeline.`,
      reflections: `Open reflection ${id} and show me the full context.`,
      pearls:      `Open pearl ${id} and show me where it applied.`,
      imaging:     `Open imaging ${id} and describe the key findings.`,
    };
    handleSend(bykind[kind] || `Tell me more about ${id}.`);
  };

  const handleSend = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (empty) setActiveConversationId("CV-2145");

    // Structured intent routing — bypasses the canned answer for
    // recognised commands (Add to M&M, log new case, …).
    const intent = matchNi2Intent(trimmed);
    if (intent) {
      const confId = `NI2-INTENT-${Date.now()}`;
      setTurns((prev) => [
        ...prev,
        { role: "surgeon", text: trimmed },
        { role: "ni2", card: { type: intent.intent, confId, payload: intent.payload } },
      ]);
      autoScrollRef.current = true;
      setAnimGen((g) => g + 1);
      return;
    }

    // Live GPT path — streams into a synthetic Ni² turn. If it
    // errors, the store still contains the surgeon turn so the user
    // sees what they sent, and the caller can retry.
    if (liveActive) {
      const streamTurn = { role: "ni2", live: true, streaming: true, streamText: "", tools: [], progress: [], scope: ni2Scope, scopeLabel: ni2ScopeDisplayLabel };
      const newTurns = [...turns, { role: "surgeon", text: trimmed }, streamTurn];
      setTurns(newTurns);
      autoScrollRef.current = true;

      const streamIdx = newTurns.length - 1;
      const messages = newTurns
        .filter((t, i) => i < streamIdx && (t.role === "surgeon" || (t.role === "ni2" && t.streamText)))
        .map((t) => ({
          role: t.role === "surgeon" ? "user" : "assistant",
          content: t.role === "surgeon" ? t.text : (t.streamText || ""),
        }));

      streamNi2Chat({
        sessionId: activeConversationId || "CV-2145",
        messages,
        activeCaseId: "VS-RAO-001",
        activeImagingId: activeImagingId || null,
        activeImagingPlaceholder: activeImagingPlaceholder || null,
        scope: ni2Scope,
        currentUserId: "dr_rao",
        onTools: (tools) => setTurns((prev) => prev.map((t, i) => (i === streamIdx ? { ...t, tools } : t))),
        onProgress: (evt) => setTurns((prev) => prev.map((t, i) => {
          if (i !== streamIdx) return t;
          const next = [...(t.progress || [])];
          // Merge start/done events per stage so we don't accumulate duplicates.
          if (evt.state === "start") {
            if (!next.find((p) => p.stage === evt.stage)) {
              next.push({ stage: evt.stage, state: "start", label: evt.label });
            }
          } else if (evt.state === "done") {
            const idx = next.findIndex((p) => p.stage === evt.stage);
            const entry = { stage: evt.stage, state: "done", label: evt.label, count: evt.count, cached: evt.cached };
            if (idx >= 0) next[idx] = entry; else next.push(entry);
          }
          return { ...t, progress: next };
        })),
        onDelta: (chunk) => setTurns((prev) => prev.map((t, i) => (i === streamIdx ? { ...t, streamText: (t.streamText || "") + chunk } : t))),
        onDone: () => setTurns((prev) => prev.map((t, i) => (i === streamIdx ? { ...t, streaming: false } : t))),
        onError: (err) => setTurns((prev) => prev.map((t, i) => (i === streamIdx ? { ...t, streaming: false, streamError: err } : t))),
      });
      return;
    }

    setTurns((prev) => [
      ...prev,
      { role: "surgeon", text: trimmed },
      { role: "ni2", ...CANNED_RESPONSE },
    ]);
    autoScrollRef.current = true;
    setAnimGen((g) => g + 1);
  };

  const lastNi2Index = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "ni2") return i;
    }
    return -1;
  }, [turns]);

  /* -------- Source preview (provenance deep-link) --------
     Interaction hierarchy:
       Provenance chip
         → Inline Evidence Preview (expands beneath the owning answer)
           → optional Full Source workspace (secondary action)
     Only one inline preview may be expanded at a time. Clicking the
     same chip toggles it closed; clicking another chip collapses the
     current preview then smoothly expands the new one. */
  const [expandedChip, setExpandedChip] = useState(null);
  const [expandedOpen, setExpandedOpen] = useState(false);
  const [justOpenedRef, setJustOpenedRef] = useState(null);
  const [fullSource, setFullSource] = useState(null);
  const closeTimerRef = useRef(null);
  const replaceTimerRef = useRef(null);
  const pullTimerRef = useRef(null);

  const clearPendingTimers = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    if (replaceTimerRef.current) { clearTimeout(replaceTimerRef.current); replaceTimerRef.current = null; }
  };

  const triggerChipPull = (ref, turnIndex) => {
    if (pullTimerRef.current) clearTimeout(pullTimerRef.current);
    setJustOpenedRef({ ref, turnIndex });
    pullTimerRef.current = setTimeout(() => setJustOpenedRef(null), 280);
  };

  const openSource = (chip /* , event */) => {
    // Same chip (same turn) → toggle close
    if (
      expandedChip &&
      expandedChip.ref === chip.ref &&
      expandedChip.turnIndex === chip.turnIndex
    ) {
      closeInline();
      return;
    }
    clearPendingTimers();
    triggerChipPull(chip.ref, chip.turnIndex);

    // Currently open with a different chip → collapse, then expand new
    if (expandedChip && expandedOpen) {
      setExpandedOpen(false);
      replaceTimerRef.current = setTimeout(() => {
        setExpandedChip(chip);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setExpandedOpen(true))
        );
      }, 230);
      return;
    }

    // Fresh open
    setExpandedChip(chip);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setExpandedOpen(true))
    );
  };

  const closeInline = () => {
    clearPendingTimers();
    setExpandedOpen(false);
    closeTimerRef.current = setTimeout(() => setExpandedChip(null), 240);
  };

  const openFullFromInline = () => {
    if (!expandedChip) return;
    const chip = expandedChip;
    closeInline();
    setFullSource(chip);
  };
  const closeFullSource = () => setFullSource(null);

  // Escape closes whichever inspector is currently open.
  useEffect(() => {
    if (!expandedChip && !fullSource) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (fullSource) setFullSource(null);
      else closeInline();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expandedChip, fullSource]);

  /* -------- Follow-up suggestion handler -------- */
  const handleFollowUp = (text) => {
    handleSend(text);
  };

  /* -------- Provenance-workspace helpers -------- */
  const askAboutRecord = ({ type, id }) => {
    const map = {
      case:       `Tell me more about case ${id}.`,
      reflection: `Open reflection ${id} and unpack the key lesson.`,
      pearl:      `Where has pearl ${id} applied in my recent cases?`,
      mm:         `Summarise the departmental lessons from M&M ${id}.`,
      imaging:    `Describe imaging ${id} and its clinical implications.`,
      literature: `Summarise the key findings from paper ${id}.`,
    };
    handleSend(map[type] || `Tell me more about ${id}.`);
  };
  const openFullPage = (href) => {
    if (typeof window !== "undefined") window.location.assign(href);
  };

  /* -------- Divider drag (splitter) -------- */
  const workspaceRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const container = workspaceRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const pct = (x / rect.width) * 100;
      setLeftPct(pct);
    };
    const onUp = () => setDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [dragging, setLeftPct]);

  const splitActive = provOpen && !isNarrow;
  const leftColStyle = splitActive
    ? { width: `${leftPct}%`, minWidth: 0 }
    : { flex: 1, minWidth: 0 };

  return (
    <div
      ref={workspaceRef}
      className="relative flex gap-3"
      style={{ height: WORKSPACE_HEIGHT }}
      data-testid="ni2-workspace-root"
      data-split={splitActive || undefined}
    >
      <div className="flex flex-col" style={leftColStyle}>
      <header
        className="flex shrink-0 items-center justify-between gap-3 px-1 pb-3"
        style={{
          background:
            "linear-gradient(180deg, rgba(14,20,34,0.55) 0%, rgba(14,20,34,0.35) 60%, transparent 100%)",
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="nn-btn nn-btn-ghost"
            data-testid="ni2-back-to-dashboard"
            aria-label="Back to Dashboard"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            Back to Dashboard
          </button>
          <span className="text-[hsl(var(--nn-ink-faint))]">/</span>
          <Ni2Icon size={20} />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold tracking-tight text-white">
              {empty ? "New conversation" : ACTIVE_CONVERSATION.title}
            </div>
            <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {empty ? "Ni² · Ready to listen" : ACTIVE_CONVERSATION.status}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="nn-chip nn-chip-blue">Provenance on</span>
          <span className="nn-chip">Voice ready</span>
          {!libraryOpen && !splitActive && (
            <button
              onClick={() => setLibraryOpen(true)}
              className="nn-btn nn-btn-ghost"
              data-testid="my-library-open"
              title="Open My Library"
            >
              <BrainCircuit className="h-3.5 w-3.5" strokeWidth={1.8} />
              My Library
            </button>
          )}
          <button
            onClick={() => setActiveConversationId(empty ? "CV-2145" : null)}
            className="nn-btn nn-btn-ghost"
            title="Toggle empty state"
            data-testid="ni2-toggle-sample"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
            {empty ? "Show sample" : "New"}
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        data-testid="ni2-conversation-scroll"
        className="nn-glass nn-scroll min-h-0 flex-1 overflow-y-auto"
        style={{ padding: "20px 28px 12px 28px" }}
      >
        {empty ? (
          <EmptyState onSuggestion={handleSend} />
        ) : (
          <ConversationThread
            turns={turns}
            lastNi2Index={lastNi2Index}
            animGen={animGen}
            requestAutoScroll={() => scrollToBottom(true)}
            openSource={openSource}
            onFollowUp={handleFollowUp}
            onLiveFollowUp={handleLiveFollowUp}
            onOpenChip={(chip) => openProvenance(chip.category || classifyProvenanceId(chip.id), chip.id)}
            demoMode={demoMode}
            activeChip={expandedChip}
            justOpenedRef={justOpenedRef}
            expandedChip={expandedChip}
            expandedOpen={expandedOpen}
            onCloseInline={closeInline}
            onOpenFull={openFullFromInline}
          />
        )}
        {!empty && <div style={{ height: 40 }} aria-hidden="true" />}
      </div>

      <div className="shrink-0 pt-3">
        <Composer onSend={handleSend} onOpenFiles={onOpenFiles} filesActive={filesActive} />
      </div>

      {fullSource && (
        <SourceFullView
          source={fullSource}
          onClose={closeFullSource}
        />
      )}
      </div>

      {/* Splitter — draggable divider between conversation & provenance */}
      {splitActive && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize record pane"
          data-testid="ni2-splitter"
          onMouseDown={() => setDragging(true)}
          onTouchStart={() => setDragging(true)}
          className="group relative flex w-1.5 shrink-0 cursor-col-resize items-center justify-center"
          title="Drag to resize"
        >
          <span
            className="h-16 w-0.5 rounded-full transition-colors"
            style={{ background: dragging ? "hsl(var(--nn-blue))" : "rgba(255,255,255,0.14)" }}
          />
        </div>
      )}

      {/* Right pane: provenance workspace (wide) OR My Library rail */}
      {splitActive ? (
        <div
          className="flex flex-col"
          style={{ width: `${100 - leftPct}%`, minWidth: 0 }}
          data-testid="provenance-workspace-slot"
        >
          <ProvenanceWorkspacePanel
            active={provActive}
            onClose={closeProvenance}
            onOpenAnother={(t, id) => openProvenance(t, id)}
            onAskAboutRecord={askAboutRecord}
            onOpenFullPage={openFullPage}
          />
        </div>
      ) : (
        !isNarrow && libraryOpen && !provOpen && (
          <Ni2MyLibraryPanel
            open={libraryOpen}
            onToggle={setLibraryOpen}
            onOpenRecord={handleLibraryOpenRecord}
          />
        )
      )}

      {/* Narrow-viewport sheet — provenance renders as full-screen overlay */}
      {provOpen && isNarrow && (
        <div
          className="nn-soft-in fixed inset-0 z-50 flex flex-col p-3"
          style={{
            background: "rgba(10,14,24,0.86)",
            backdropFilter: "blur(28px) saturate(160%)",
            WebkitBackdropFilter: "blur(28px) saturate(160%)",
          }}
          data-testid="provenance-workspace-sheet"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={closeProvenance}
              className="nn-btn nn-btn-ghost"
              data-testid="provenance-workspace-back-narrow"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
              Back to Ni²
            </button>
            <span className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--nn-ink-faint))]">
              Record detail
            </span>
          </div>
          <div className="min-h-0 flex-1">
            <ProvenanceWorkspacePanel
              active={provActive}
              onClose={closeProvenance}
              onOpenAnother={(t, id) => openProvenance(t, id)}
              onAskAboutRecord={askAboutRecord}
              onOpenFullPage={openFullPage}
              isNarrow
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------ Empty state ------------------ */
function EmptyState({ onSuggestion }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 py-10 text-center">
      <div
        className="grid h-16 w-16 place-items-center rounded-2xl"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Ni2Icon size={42} />
      </div>
      <div className="flex items-center gap-2 text-[hsl(var(--nn-blue))]">
        <span className="nn-pulse-dot" style={{ background: "hsl(var(--nn-blue))" }} />
        <span className="text-[11px] font-medium uppercase tracking-[0.18em]">
          Ni² · listening
        </span>
      </div>
      <h2 className="text-[24px] font-semibold tracking-tight text-white">
        Hey, Dr. Rao. How can I help?
      </h2>
      <p className="max-w-xl text-[13.5px] leading-[21px] text-[hsl(var(--nn-ink-muted))]">
        Ask about a case, dictate a new observation, retrieve prior surgical memory,
        or begin a pre-case review.
      </p>
      <div className="mt-2 flex flex-wrap justify-center gap-1.5">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            className="nn-chip transition-colors hover:text-[hsl(var(--nn-ink))]"
            onClick={() => onSuggestion?.(s)}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------ Conversation thread ------------------ */
function ConversationThread({
  turns,
  lastNi2Index,
  animGen,
  requestAutoScroll,
  openSource,
  onFollowUp,
  onLiveFollowUp,
  onOpenChip,
  demoMode,
  activeChip,
  justOpenedRef,
  expandedChip,
  expandedOpen,
  onCloseInline,
  onOpenFull,
}) {
  return (
    <div className="mx-auto flex max-w-[780px] flex-col gap-6">
      {turns.map((t, i) => (
        <div
          key={i}
          className={`flex w-full ${t.role === "surgeon" ? "justify-end" : "justify-start"}`}
        >
          {t.role === "surgeon" ? (
            <SurgeonBubble text={t.text} />
          ) : t.card?.type === "ADD_TO_MM" ? (
            <MMConfirmationCard confId={t.card.confId} payload={t.card.payload} />
          ) : t.card?.type === "RECALL_DEPT_MEMORY" ? (
            <MMDepartmentMemoryCard payload={t.card.payload} />
          ) : t.live ? (
            <Ni2LiveBubble
              turn={t}
              demoMode={demoMode}
              onOpenChip={onOpenChip}
              onFollowUp={onLiveFollowUp}
            />
          ) : i === lastNi2Index ? (
            <Ni2AnimatedBubble
              key={`animated-${animGen}-${i}`}
              turn={t}
              turnIndex={i}
              requestAutoScroll={requestAutoScroll}
              openSource={openSource}
              onFollowUp={onFollowUp}
              activeChip={activeChip}
              justOpenedRef={justOpenedRef}
              expandedChip={expandedChip}
              expandedOpen={expandedOpen}
              onCloseInline={onCloseInline}
              onOpenFull={onOpenFull}
            />
          ) : (
            <Ni2StaticBubble
              text={t.text}
              cards={t.cards}
              provenance={t.provenance}
              turnIndex={i}
              openSource={openSource}
              activeChip={activeChip}
              justOpenedRef={justOpenedRef}
              expandedChip={expandedChip}
              expandedOpen={expandedOpen}
              onCloseInline={onCloseInline}
              onOpenFull={onOpenFull}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function SurgeonBubble({ text }) {
  return (
    <div
      className="max-w-[68%] rounded-2xl px-4 py-2.5 nn-soft-in"
      style={{
        background: "linear-gradient(180deg, rgba(140,180,250,0.22), rgba(100,140,220,0.14))",
        border: "1px solid rgba(160,195,255,0.28)",
        color: "hsl(var(--nn-ink))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
      }}
    >
      <div className="text-[13.5px] leading-[21px]">{text}</div>
    </div>
  );
}

/* Live-streamed Ni² bubble — real GPT tokens arriving over SSE.
   Uses `Ni2StructuredAnswer` to render the four-part demo layout
   (Recommendation → collapsible evidence → Source Summary card →
   Follow-up actions). In Demo Mode, we replace the developer tool
   chip strip with a single human-readable retrieval banner. */
function Ni2LiveBubble({ turn, onOpenChip, onFollowUp, demoMode = true }) {
  const { streamText = "", streaming, tools = [], streamError, scopeLabel, scope = "personal", progress = [] } = turn;
  const bannerLabel = scopeToBannerLabel(scope, tools);
  const hasProgress = (progress || []).length > 0;
  const synthesisStarted = (progress || []).some((p) => p.stage === "synthesis" && p.state === "start");
  const hasStructuredText = !!streamText && /\*\*\s*Recommendation/i.test(streamText);
  // Keep the progress rail visible until we actually have the
  // Recommendation section rendering — then it fades out.
  const showProgress = hasProgress && !hasStructuredText;
  return (
    <div
      className="max-w-[76%] rounded-2xl border px-4 py-3 nn-soft-in"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        borderColor: "rgba(255,255,255,0.10)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
      }}
      data-testid="ni2-live-bubble"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--nn-ok))]" />
          Live GPT · openai/gpt-5.6-sol
        </span>
        {!demoMode && tools.length > 0 && (
          <span>· {tools.length} tool{tools.length === 1 ? "" : "s"}</span>
        )}
        {scopeLabel && (
          <span
            className="ml-auto inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 normal-case tracking-normal"
            style={{
              background: "linear-gradient(180deg, rgba(160,200,255,0.14), rgba(120,165,240,0.04))",
              borderColor: "rgba(160,200,255,0.30)",
              color: "hsl(var(--nn-ink))",
              fontSize: 10,
              letterSpacing: 0,
            }}
            data-testid="ni2-live-scope-label"
          >
            <span className="text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">Source</span>
            {scopeLabel}
          </span>
        )}
      </div>

      {/* Demo-mode retrieval banner replaces the raw dev-tool chip list. */}
      {demoMode ? (
        tools.length > 0 && (
          <div
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px]"
            style={{
              background: "rgba(160,200,255,0.10)",
              borderColor: "rgba(160,200,255,0.28)",
              color: "hsl(var(--nn-ink))",
            }}
            data-testid="ni2-retrieval-banner"
          >
            <span className="inline-flex h-1.5 w-1.5 rounded-full" style={{ background: "hsl(var(--nn-blue))" }} />
            {bannerLabel}
          </div>
        )
      ) : (
        tools.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid="ni2-dev-tool-strip">
            {tools.map((t, i) => (
              <span
                key={i}
                className={
                  "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9.5px] font-mono " +
                  (t.ok
                    ? "border-[hsl(var(--nn-ok))]/40 bg-[hsl(var(--nn-ok))]/10 text-[hsl(var(--nn-ok))]"
                    : "border-[hsl(var(--nn-warn))]/40 bg-[hsl(var(--nn-warn))]/10 text-[hsl(var(--nn-warn))]")
                }
                data-testid={`ni2-live-tool-${t.name}`}
              >
                {t.name}
              </span>
            ))}
          </div>
        )
      )}

      {/* Progress rail — live retrieval stages before the Recommendation streams in */}
      {showProgress && (
        <div className="mt-2.5 flex flex-col gap-1" data-testid="ni2-progress-rail">
          {progress.map((p, i) => {
            const done = p.state === "done";
            const isSynthesis = p.stage === "synthesis";
            return (
              <div
                key={`${p.stage}-${i}`}
                className="flex items-center gap-2 text-[12px] text-[hsl(var(--nn-ink))]"
                data-testid={`ni2-progress-${p.stage}-${p.state}`}
              >
                <span
                  className={"inline-flex h-3 w-3 items-center justify-center rounded-full " + (done ? "bg-[hsl(var(--nn-ok))]/25 text-[hsl(var(--nn-ok))]" : (isSynthesis ? "bg-[hsl(var(--nn-blue))]/25" : "bg-white/[0.06]"))}
                  aria-hidden="true"
                >
                  {done ? (
                    <span className="text-[9px] leading-none">✓</span>
                  ) : (
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: isSynthesis ? "hsl(var(--nn-blue))" : "rgba(255,255,255,0.5)" }} />
                  )}
                </span>
                <span className={done ? "text-[hsl(var(--nn-ink))]" : "text-[hsl(var(--nn-ink-muted))]"}>{p.label}</span>
                {p.cached && (
                  <span className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[hsl(var(--nn-ink-faint))]">cached</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-2.5">
        <Ni2StructuredAnswer
          streamText={streamText}
          streaming={streaming}
          tools={tools}
          scope={scope}
          demoMode={demoMode}
          onOpenChip={onOpenChip}
          onFollowUp={onFollowUp}
          turnKey={turn.__key || streamText.slice(0, 40)}
          synthesisStarted={synthesisStarted}
        />
      </div>

      {streamError && (
        <div className="mt-2 rounded-lg border border-[hsl(var(--nn-warn))]/30 bg-[hsl(var(--nn-warn))]/10 px-2 py-1 text-[10.5px] text-[hsl(var(--nn-warn))]" data-testid="ni2-live-error">
          Live GPT unavailable — switch off the Live toggle to fall back to the deterministic demo.
          <div className="mt-0.5 font-mono text-[9.5px] opacity-70">{streamError}</div>
        </div>
      )}
    </div>
  );
}

/* Static Ni² bubble — prior turns render fully composed. */
function Ni2StaticBubble({
  text, cards, provenance, turnIndex,
  openSource, activeChip, justOpenedRef,
  expandedChip, expandedOpen, onCloseInline, onOpenFull,
}) {
  const ownsExpanded =
    !!expandedChip && expandedChip.turnIndex === turnIndex;
  return (
    <div className="flex max-w-[80%] items-start gap-3">
      <Ni2Icon size={26} />
      <div className="min-w-0 flex-1">
        <div className="nn-eyebrow mb-1">Ni²</div>
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
          <p className="text-[14px] leading-[22px] text-[hsl(var(--nn-ink))]">
            {text}
          </p>
          {cards && cards.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {cards.map((c, i) => <InlineCard key={i} card={c} />)}
            </div>
          )}
          {provenance && provenance.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {provenance.map((p, i) => (
                <ProvenanceTag
                  key={i}
                  kind={p.kind}
                  label={p.label}
                  ref={p.ref}
                  active={
                    !!activeChip &&
                    activeChip.turnIndex === turnIndex &&
                    activeChip.ref === p.ref
                  }
                  justOpened={
                    !!justOpenedRef &&
                    justOpenedRef.turnIndex === turnIndex &&
                    justOpenedRef.ref === p.ref
                  }
                  onClick={(e) => openSource?.({ ...p, turnIndex }, e)}
                />
              ))}
            </div>
          )}
        </div>
        <BubbleActions />
        <div
          className="nn-inline-expand"
          data-open={(ownsExpanded && expandedOpen) || undefined}
          aria-hidden={!ownsExpanded}
        >
          <div className="nn-inline-inner">
            {ownsExpanded && (
              <InlineEvidencePreview
                chip={expandedChip}
                onClose={onCloseInline}
                onOpenFull={onOpenFull}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------ Streaming schedule ------------------ */
/* Precompute per-word cadence so streaming feels like an expert composing
   an answer rather than a mechanical typer. Longer pauses land after
   sentence terminators, citations and paragraph breaks. */
function buildStreamSchedule(text) {
  const paragraphs = text.split(/\n\n+/);
  const schedule = []; // [{ token, delay, isBreak, cite }]
  let cursor = 0;
  paragraphs.forEach((para, pIdx) => {
    const words = para.split(/\s+/).filter(Boolean);
    words.forEach((w, wIdx) => {
      let delay = 34;
      const trimmed = w.replace(/[.,;:—]+$/g, "");
      // Citation-like tokens — matches workbook ID patterns:
      //   Cases: VS-RAO-###, VS-GRA-###, VS-PAR-###, VS-LIN-###, VS-SOR-###,
      //          CPA-MEN-###, TN-###, PIT-###, EPI-###
      //   Reflections: REF-###   Operative pearls: PEARL-###
      //   Imaging: IMG-###       Literature: LIT-###   M&M: MM-###
      const isCitation =
        /^(VS|CPA|TN|PIT|EPI)-[A-Z]{2,3}-?\d{2,4}[A-Z]?$/.test(trimmed) ||
        /^(REF|PEARL|IMG|LIT|MM|ENC|TLE)-\d{3,}$/.test(trimmed);
      // Base per-word cadence with slight jitter for naturalness.
      delay += (wIdx % 3) * 4;
      // Slight extra pause after punctuation.
      if (/[.!?]$/.test(w))  delay += 260;
      else if (/[,;:]$/.test(w)) delay += 90;
      // Extra pause after citations so the reader registers them.
      if (isCitation) delay += 160;
      schedule.push({ token: w, delay, cite: isCitation ? trimmed.replace(/[^A-Za-z0-9-]/g, "") : null });
      cursor++;
    });
    if (pIdx < paragraphs.length - 1) {
      schedule.push({ token: "\n\n", delay: 340, isBreak: true });
    }
  });
  return schedule;
}

/* Given a citation-token, return the sidebar conversation id to flash. */
function convForCitation(token) {
  if (!token) return null;
  // Direct match against known refs
  if (CITATION_TO_CONV[token]) return CITATION_TO_CONV[token];
  // Prefix match against ref bases (e.g., MM-2024 for MM-2024-14)
  const keys = Object.keys(CITATION_TO_CONV);
  for (const k of keys) {
    if (token.startsWith(k)) return CITATION_TO_CONV[k];
  }
  return null;
}

/* ------------------ Animated Ni² bubble ------------------ */
function Ni2AnimatedBubble({
  turn, turnIndex, requestAutoScroll, openSource, onFollowUp,
  activeChip, justOpenedRef,
  expandedChip, expandedOpen, onCloseInline, onOpenFull,
}) {
  const { flashConv } = useWorkspaceMode();

  // phase: "retrieval" | "synthesis" | "streaming" | "chips" | "done"
  const [phase, setPhase] = useState("retrieval");
  const [retrievalIndex, setRetrievalIndex] = useState(0);
  const [retrievalSub, setRetrievalSub] = useState("searching"); // "searching" | "found"

  const stages = turn.retrieval || [];
  const schedule = useMemo(() => buildStreamSchedule(turn.text || ""), [turn.text]);
  const totalStreamMs = useMemo(
    () => schedule.reduce((a, s) => a + s.delay, 0) + 220,
    [schedule]
  );

  /* -------- Retrieval sequencer -------- */
  useEffect(() => {
    if (phase !== "retrieval") return;
    if (retrievalIndex >= stages.length) {
      const t = setTimeout(() => setPhase("synthesis"), 220);
      return () => clearTimeout(t);
    }
    // Each stage: 500ms searching → 300ms show found (with count) → next
    if (retrievalSub === "searching") {
      const t = setTimeout(() => setRetrievalSub("found"), 520);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setRetrievalIndex((i) => i + 1);
        setRetrievalSub("searching");
      }, 320);
      return () => clearTimeout(t);
    }
  }, [phase, retrievalIndex, retrievalSub, stages.length]);

  /* -------- Synthesis -> Streaming -------- */
  useEffect(() => {
    if (phase !== "synthesis") return;
    const t = setTimeout(() => setPhase("streaming"), 700);
    return () => clearTimeout(t);
  }, [phase]);

  /* -------- Word-by-word streaming with citation flash -------- */
  const [streamedWords, setStreamedWords] = useState([]);
  useEffect(() => {
    if (phase !== "streaming") return;
    setStreamedWords([]);
    const timeouts = [];
    let acc = 0;
    schedule.forEach((step) => {
      acc += step.delay;
      const t = setTimeout(() => {
        setStreamedWords((prev) => [...prev, step]);
        if (step.cite) {
          const conv = convForCitation(step.cite);
          if (conv) flashConv(conv, 900);
        }
      }, acc);
      timeouts.push(t);
    });
    const done = setTimeout(() => setPhase("chips"), acc + 260);
    timeouts.push(done);
    return () => timeouts.forEach(clearTimeout);
  }, [phase, schedule, flashConv]);

  /* -------- Chips + confidence -> done -------- */
  useEffect(() => {
    if (phase !== "chips") return;
    const chipDur = (turn.provenance?.length || 0) * 120 + 320;
    const t = setTimeout(() => setPhase("done"), chipDur);
    return () => clearTimeout(t);
  }, [phase, turn.provenance]);

  /* -------- Auto-follow scroll -------- */
  useEffect(() => {
    requestAutoScroll?.();
  }, [phase, retrievalIndex, retrievalSub, requestAutoScroll]);

  useEffect(() => {
    if (phase !== "streaming") return;
    const id = setInterval(() => requestAutoScroll?.(), 220);
    return () => clearInterval(id);
  }, [phase, requestAutoScroll]);

  useEffect(() => { requestAutoScroll?.(); }, [streamedWords.length, requestAutoScroll]);

  const showChipsAndConfidence =
    (phase === "chips" || phase === "done") &&
    turn.provenance &&
    turn.provenance.length > 0;

  return (
    <div className="flex max-w-[80%] items-start gap-3">
      <Ni2Icon size={26} />
      <div className="min-w-0 flex-1">
        <div className="nn-eyebrow mb-1">Ni²</div>
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
          {phase === "retrieval" && (
            <RetrievalPanel
              stages={stages}
              activeIndex={retrievalIndex}
              activeSub={retrievalSub}
            />
          )}

          {phase === "synthesis" && (
            <SynthesisPanel
              summary={turn.synthesis?.summary || []}
              confidence={turn.synthesis?.confidence || turn.confidence || "High"}
            />
          )}

          {(phase === "streaming" || phase === "chips" || phase === "done") && (
            <>
              <StreamedParagraph words={streamedWords} />
              {showChipsAndConfidence && (
                <div className="mt-3 flex flex-col gap-2">
                  <ConfidenceBadge value={turn.confidence || turn.synthesis?.confidence || "High"} />
                  <div
                    className="flex flex-wrap items-center gap-1.5"
                    data-testid="ni2-provenance-row"
                  >
                    {turn.provenance.map((p, i) => (
                      <span
                        key={i}
                        className="nn-chip-connect"
                        style={{ animationDelay: `${i * 120}ms` }}
                      >
                        <ProvenanceTag
                          kind={p.kind}
                          label={p.label}
                          ref={p.ref}
                          active={
                            !!activeChip &&
                            activeChip.turnIndex === turnIndex &&
                            activeChip.ref === p.ref
                          }
                          justOpened={
                            !!justOpenedRef &&
                            justOpenedRef.turnIndex === turnIndex &&
                            justOpenedRef.ref === p.ref
                          }
                          onClick={(e) => openSource?.({ ...p, turnIndex }, e)}
                        />
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {phase === "done" && turn.followups && turn.followups.length > 0 && (
                <FollowUpRow items={turn.followups} onPick={onFollowUp} />
              )}
            </>
          )}
        </div>
        {(phase === "streaming" || phase === "chips" || phase === "done") && (
          <BubbleActions />
        )}
        <div
          className="nn-inline-expand"
          data-open={(!!expandedChip && expandedChip.turnIndex === turnIndex && expandedOpen) || undefined}
          aria-hidden={!(expandedChip && expandedChip.turnIndex === turnIndex)}
        >
          <div className="nn-inline-inner">
            {expandedChip && expandedChip.turnIndex === turnIndex && (
              <InlineEvidencePreview
                chip={expandedChip}
                onClose={onCloseInline}
                onOpenFull={onOpenFull}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------ Follow-up suggestions ------------------ */
function FollowUpRow({ items, onPick }) {
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-1.5"
      data-testid="ni2-followup-row"
    >
      <span className="nn-eyebrow mr-1">Follow up</span>
      {items.map((text, i) => (
        <button
          key={text}
          className="nn-followup"
          data-testid={`ni2-followup-${i}`}
          onClick={() => onPick?.(text)}
          style={{ animationDelay: `${i * 150}ms` }}
        >
          <Sparkles className="h-3 w-3 text-[hsl(var(--nn-blue))]" strokeWidth={1.8} />
          <span>{text}</span>
          <ArrowRight
            className="h-3 w-3 opacity-0 transition-opacity"
            strokeWidth={1.8}
            style={{ marginLeft: 2 }}
          />
        </button>
      ))}
    </div>
  );
}

/* ------------------ Inline Evidence Preview ------------------ */
/* Rendered inside the assistant bubble that owns the clicked chip —
   the preview physically extends the answer. All expand/collapse
   motion is handled by the .nn-inline-expand wrapper (grid-rows
   trick) so this component simply renders content. */
function InlineEvidencePreview({ chip, onClose, onOpenFull }) {
  const data = findSource(chip);

  const KindIcon =
    chip.label === "Case"       ? FolderOpen
    : chip.label === "Paper"    ? BookOpen
    : chip.label === "Imaging"  ? ImageIcon
    : chip.label === "M&M"      ? AlertOctagon
    :                             Lightbulb;
  const kindTone =
    chip.label === "Paper" || chip.label === "Imaging" ? "hsl(var(--nn-blue))"
    : chip.label === "M&M" ? "hsl(var(--nn-warn))"
    : "hsl(var(--nn-amber))";

  const ctaLabel =
    chip.label === "Case"        ? "Open full case"
    : chip.label === "Reflection"? "Open full reflection"
    : chip.label === "Imaging"   ? "Open imaging viewer"
    : chip.label === "Paper"     ? "Open literature"
    : chip.label === "M&M"       ? "Open full M&M"
    :                              "Open full source";

  return (
    <div
      role="region"
      aria-label={`${chip.label} · ${chip.ref}`}
      data-testid="ni2-inline-preview"
      className="nn-inline-preview"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
              border: "1px solid rgba(255,255,255,0.10)",
              color: kindTone,
            }}
          >
            <KindIcon className="h-3.5 w-3.5" strokeWidth={1.8} />
          </span>
          <div className="min-w-0 leading-tight">
            <div className="flex items-center gap-1.5 text-[9.5px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--nn-ink-faint))]">
              <span>{chip.label}</span>
              <span>·</span>
              <span className="font-mono text-[10px] normal-case tracking-normal text-[hsl(var(--nn-ink-muted))]">
                {chip.ref}
              </span>
            </div>
            <div className="mt-0.5 truncate text-[12.5px] font-semibold tracking-tight text-white">
              {data?.title || chip.ref}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-[hsl(var(--nn-ink-muted))] transition-colors hover:bg-white/[0.06] hover:text-[hsl(var(--nn-ink))]"
          aria-label="Close preview"
          data-testid="ni2-inline-close"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      </div>

      {/* Body — reuses the compact per-kind renderers. */}
      <div className="nn-scroll" style={{ padding: "12px 16px 4px 16px", maxHeight: 360, overflowY: "auto" }}>
        {data ? <QuickBody data={data} /> : <SourceMissing ref={chip.ref} />}
      </div>

      {/* Footer CTA — promotes to the full source workspace. */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
      >
        <span className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
          Esc · close
        </span>
        <button
          onClick={onOpenFull}
          className="nn-inline-cta"
          data-testid="ni2-inline-open-full"
        >
          {ctaLabel}
          <ArrowRight className="h-3 w-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

function QuickBody({ data }) {
  if (data.kind === "case")       return <QuickCase data={data} />;
  if (data.kind === "reflection") return <QuickReflection data={data} />;
  if (data.kind === "imaging")    return <QuickImaging data={data} />;
  if (data.kind === "paper")      return <QuickPaper data={data} />;
  if (data.kind === "mm")         return <QuickMM data={data} />;
  return null;
}

function QuickCase({ data }) {
  // 3-5 most recent timeline entries.
  const events = data.timeline.slice(0, 4);
  const outcome = data.header.find((h) => /outcome|complexity/i.test(h.label))?.value;
  const approach = data.header.find((h) => /approach/i.test(h.label))?.value;
  const attending = data.header.find((h) => /attending/i.test(h.label))?.value;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-0.5 text-[11.5px]">
        <div className="text-[hsl(var(--nn-ink-muted))]">
          {data.subtitle}
        </div>
        {(approach || outcome) && (
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
            {approach && (
              <span>
                <span className="text-[hsl(var(--nn-ink-faint))]">Approach </span>
                <span className="text-[hsl(var(--nn-ink))]">{approach}</span>
              </span>
            )}
            {outcome && (
              <span>
                <span className="text-[hsl(var(--nn-ink-faint))]">Outcome </span>
                <span className="text-[hsl(var(--nn-ink))]">{outcome}</span>
              </span>
            )}
            {attending && (
              <span>
                <span className="text-[hsl(var(--nn-ink-faint))]">Attending </span>
                <span className="text-[hsl(var(--nn-ink))]">{attending}</span>
              </span>
            )}
          </div>
        )}
      </div>
      <div className="nn-eyebrow mt-1">Key timeline</div>
      <ol className="relative ml-1 border-l border-white/[0.08]">
        {events.map((ev, i) => (
          <li key={i} className="relative mb-2 pl-3 last:mb-0.5">
            <span
              className="absolute -left-[4px] top-1.5 h-1.5 w-1.5 rounded-full border border-white/20"
              style={{
                background:
                  i === events.length - 1 ? "hsl(var(--nn-amber))" : "rgba(255,255,255,0.35)",
              }}
            />
            <div className="text-[9.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
              {ev.t}
            </div>
            <div className="text-[11.5px] font-medium text-[hsl(var(--nn-ink))]">
              {ev.label}
            </div>
            <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
              {ev.detail}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function QuickReflection({ data }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {data.subtitle}
      </div>
      <div className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-white/[0.03] p-2.5">
        <Quote className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--nn-amber))]" strokeWidth={1.8} />
        <p className="text-[11.5px] italic leading-[17px] text-[hsl(var(--nn-ink))]">
          {data.quote}
        </p>
      </div>
      <div className="flex flex-wrap gap-1">
        {data.tags.map((t) => (
          <span key={t} className="nn-chip" style={{ padding: "2px 8px", fontSize: 10.5 }}>
            <Tag className="h-2 w-2" strokeWidth={2} />
            {t}
          </span>
        ))}
      </div>
      {data.linkedCase && (
        <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
          Linked case:{" "}
          <span className="font-mono text-[hsl(var(--nn-ink))]">{data.linkedCase}</span>
        </div>
      )}
    </div>
  );
}

function QuickImaging({ data }) {
  const findings = data.findings.slice(0, 3);
  return (
    <div className="flex flex-col gap-2.5">
      <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {data.subtitle}
      </div>
      <div
        className="relative h-[112px] w-full overflow-hidden rounded-lg border border-white/[0.08]"
        style={{
          background:
            "radial-gradient(ellipse at 40% 45%, rgba(240,240,255,0.55), rgba(80,90,120,0.65) 45%, rgba(20,26,40,1) 80%), #101623",
        }}
      >
        <div
          className="absolute rounded-full border-2"
          style={{
            left: "38%", top: "38%", width: 30, height: 30,
            borderColor: "rgba(240, 165, 105, 0.75)",
            boxShadow: "0 0 0 3px rgba(240,165,105,0.15)",
          }}
        />
        <div
          className="absolute text-[9.5px] font-mono uppercase tracking-[0.14em]"
          style={{ left: "50%", top: "28%", color: "hsl(var(--nn-amber))" }}
        >
          IAC · 2.6 cm
        </div>
        <div className="absolute bottom-1.5 left-1.5 rounded-md border border-white/10 bg-black/40 px-1.5 py-0.5 text-[9.5px] font-mono text-white/80">
          {data.modality}
        </div>
      </div>
      <ul className="flex flex-col gap-1">
        {findings.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-2 rounded-md border border-white/[0.05] bg-white/[0.02] px-2 py-1"
          >
            <span className="min-w-[54px] text-[10px] font-medium uppercase tracking-[0.10em] text-[hsl(var(--nn-blue))]">
              {f.at}
            </span>
            <span className="text-[10.5px] text-[hsl(var(--nn-ink))]">
              {f.note}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickPaper({ data }) {
  const keys = data.keyPoints.slice(0, 4);
  return (
    <div className="flex flex-col gap-2.5">
      <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {data.subtitle}
      </div>
      <p className="text-[11.5px] leading-[17px] text-[hsl(var(--nn-ink))]">
        {data.summary}
      </p>
      <div>
        <div className="nn-eyebrow mb-1">Key findings</div>
        <ul className="flex flex-col gap-0.5">
          {keys.map((k, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-[10.5px] text-[hsl(var(--nn-ink-muted))]"
            >
              <Check className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[hsl(var(--nn-blue))]" strokeWidth={2.2} />
              <span>{k}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function QuickMM({ data }) {
  const keys = data.keyPoints.slice(0, 4);
  const complication = keys[0];
  const actions = keys.slice(1);
  return (
    <div className="flex flex-col gap-2.5">
      <div className="text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
        {data.subtitle}
      </div>
      {complication && (
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5">
          <div className="nn-eyebrow mb-0.5">Complication</div>
          <div className="text-[11.5px] text-[hsl(var(--nn-ink))]">
            {complication}
          </div>
        </div>
      )}
      <p className="text-[11px] leading-[17px] text-[hsl(var(--nn-ink))]">
        {data.summary}
      </p>
      {actions.length > 0 && (
        <div>
          <div className="nn-eyebrow mb-1">Action items</div>
          <ul className="flex flex-col gap-0.5">
            {actions.map((a, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[10.5px] text-[hsl(var(--nn-ink-muted))]"
              >
                <AlertOctagon className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[hsl(var(--nn-warn))]" strokeWidth={1.8} />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------ Source Full View (secondary) ------------------ */
/* Kept intentionally similar to the previous large modal, but only
   reachable via the Quick Preview's "Open full X →" CTA. */
function SourceFullView({ source, onClose }) {
  const data = findSource(source);
  const kindIcon =
    source.label === "Case"       ? FolderOpen
    : source.label === "Paper"    ? BookOpen
    : source.label === "Imaging"  ? ImageIcon
    : source.label === "M&M"      ? AlertOctagon
    :                              Lightbulb;
  const kindTone =
    source.label === "Paper"    ? "hsl(var(--nn-blue))"
    : source.label === "Imaging"? "hsl(var(--nn-blue))"
    : source.label === "M&M"    ? "hsl(var(--nn-warn))"
    :                            "hsl(var(--nn-amber))";
  const Kind = kindIcon;

  return (
    <>
      <div
        className="nn-source-scrim"
        onClick={onClose}
        data-testid="ni2-source-scrim"
      />
      <div
        className="nn-source-panel"
        role="dialog"
        aria-label={`${source.label} · ${source.ref}`}
        data-testid="ni2-source-panel"
        style={{
          "--nn-source-origin-x": source.originX,
          "--nn-source-origin-y": source.originY,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between gap-3 px-5 py-4"
          style={{
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), transparent)",
          }}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="grid h-8 w-8 shrink-0 place-items-center rounded-xl"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
                border: "1px solid rgba(255,255,255,0.10)",
                color: kindTone,
              }}
            >
              <Kind className="h-4 w-4" strokeWidth={1.8} />
            </span>
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-2">
                <span className="nn-eyebrow">{source.label}</span>
                <span className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                  {source.ref}
                </span>
              </div>
              <div className="truncate text-[14px] font-semibold tracking-tight text-white">
                {data?.title || source.ref}
              </div>
              {data?.subtitle && (
                <div className="truncate text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                  {data.subtitle}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="nn-icon-btn"
            aria-label="Close source preview"
            data-testid="ni2-source-close"
          >
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        {/* Body */}
        <div className="nn-source-body">
          {data ? <SourceBody data={data} /> : <SourceMissing ref={source.ref} />}
        </div>
      </div>
    </>
  );
}

function SourceMissing({ ref }) {
  return (
    <div className="py-6 text-center text-[13px] text-[hsl(var(--nn-ink-muted))]">
      No prototype record for <span className="font-mono">{ref}</span> yet.
    </div>
  );
}

function SourceBody({ data }) {
  if (data.kind === "case")       return <CaseSource data={data} />;
  if (data.kind === "reflection") return <ReflectionSource data={data} />;
  if (data.kind === "imaging")    return <ImagingSource data={data} />;
  if (data.kind === "paper")      return <PaperSource data={data} />;
  if (data.kind === "mm")         return <MMSource data={data} />;
  return null;
}

function CaseSource({ data }) {
  return (
    <div className="flex flex-col gap-5">
      {/* Header cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {data.header.map((h) => {
          const HeaderIcon = h.Icon || iconForCaseHeader(h.label);
          return (
            <div
              key={h.label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-2.5"
            >
              <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                <HeaderIcon className="h-2.5 w-2.5" strokeWidth={1.8} />
                {h.label}
              </div>
              <div className="mt-1 text-[12.5px] text-[hsl(var(--nn-ink))]">
                {h.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Timeline */}
      <CaseSectionHeader label="Surgical memory timeline" />
      <ol className="relative ml-2 border-l border-white/[0.08]">
        {data.timeline.map((ev, i) => (
          <li key={i} className="relative mb-3 pl-4 last:mb-1">
            <span
              className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full border border-white/20"
              style={{
                background:
                  i === data.timeline.length - 1
                    ? "hsl(var(--nn-amber))"
                    : "rgba(255,255,255,0.35)",
              }}
            />
            <div className="text-[10px] font-mono text-[hsl(var(--nn-ink-faint))]">
              {ev.t}
            </div>
            <div className="text-[12.5px] font-medium text-[hsl(var(--nn-ink))]">
              {ev.label}
            </div>
            <div className="text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
              {ev.detail}
            </div>
          </li>
        ))}
      </ol>

      {/* Clinical Encounters */}
      {data.encounters && data.encounters.length > 0 && (
        <>
          <CaseSectionHeader
            label="Clinical encounters"
            count={data.encounters.length}
          />
          <ul className="flex flex-col gap-1.5">
            {data.encounters.map((e) => (
              <li
                key={e.encounterId}
                className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="font-medium text-[hsl(var(--nn-ink))]">
                    {e.encounterType}
                  </span>
                  <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                    {e.encounterDate} · {e.relativeTiming}
                  </span>
                </div>
                <div className="mt-0.5 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                  {e.chiefConcern}
                </div>
                {e.assessment && (
                  <div className="mt-1 text-[11.5px] text-[hsl(var(--nn-ink))]">
                    <span className="text-[hsl(var(--nn-ink-faint))]">Assessment · </span>
                    {e.assessment}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Reflections */}
      {data.reflections && data.reflections.length > 0 && (
        <>
          <CaseSectionHeader
            label="Reflections"
            count={data.reflections.length}
          />
          <ul className="flex flex-col gap-1.5">
            {data.reflections.slice(0, 4).map((r) => (
              <li
                key={r.reflectionId}
                className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2"
              >
                <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                  <Lightbulb className="h-2.5 w-2.5" strokeWidth={1.8} />
                  <span>{r.reflectionType}</span>
                  <span>·</span>
                  <span className="font-mono normal-case tracking-normal">{r.reflectionId}</span>
                  <span className="ml-auto font-mono text-[10px] normal-case tracking-normal text-[hsl(var(--nn-ink-muted))]">
                    {r.dateCaptured}
                  </span>
                </div>
                <p className="mt-1 text-[12px] italic leading-[18px] text-[hsl(var(--nn-ink))]">
                  {r.text}
                </p>
                {r.tags?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.tags.slice(0, 6).map((t) => (
                      <span key={t} className="nn-chip" style={{ padding: "2px 8px", fontSize: 10.5 }}>
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Imaging */}
      {data.imaging && data.imaging.length > 0 && (
        <>
          <CaseSectionHeader label="Imaging" count={data.imaging.length} />
          <ul className="flex flex-col gap-1.5">
            {data.imaging.map((img) => (
              <li
                key={img.imagingId}
                className="flex items-start gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2"
              >
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-white/10 bg-white/[0.04] text-[hsl(var(--nn-blue))]">
                  <ImageIcon className="h-3 w-3" strokeWidth={1.8} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11.5px]">
                    <span className="font-medium text-[hsl(var(--nn-ink))]">
                      {img.modality} · {img.sequence}
                    </span>
                    <span className="font-mono text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
                      {img.studyDate} · {img.imagingId}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[hsl(var(--nn-ink-muted))]">
                    {img.keyFinding}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* M&M */}
      {data.mm && data.mm.length > 0 && (
        <>
          <CaseSectionHeader label="M&M discussion" count={data.mm.length} />
          {data.mm.map((m) => (
            <div
              key={m.mAndMId}
              className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2.5"
            >
              <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                <AlertOctagon className="h-2.5 w-2.5" strokeWidth={1.8} />
                <span>{m.mAndMId}</span>
                <span>·</span>
                <span className="normal-case tracking-normal">{m.status}</span>
                <span className="ml-auto font-mono text-[10px] normal-case tracking-normal">{m.meetingDate}</span>
              </div>
              <div className="mt-1 text-[12px] font-medium text-[hsl(var(--nn-ink))]">
                {m.complication}
              </div>
              {m.keyDecisionPoint && (
                <div className="mt-0.5 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                  Decision · {m.keyDecisionPoint}
                </div>
              )}
              {m.learningPoints && (
                <div className="mt-0.5 text-[11.5px] text-[hsl(var(--nn-ink))]">
                  <span className="text-[hsl(var(--nn-ink-faint))]">Learning · </span>
                  {m.learningPoints}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Literature */}
      {data.literature && data.literature.length > 0 && (
        <>
          <CaseSectionHeader label="Referenced literature" count={data.literature.length} />
          <ul className="flex flex-col gap-1.5">
            {data.literature.map((l) => (
              <li
                key={l.literatureId}
                className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2"
              >
                <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                  <BookOpen className="h-2.5 w-2.5" strokeWidth={1.8} />
                  <span className="font-mono normal-case tracking-normal">{l.literatureId}</span>
                </div>
                <div className="mt-1 text-[12px] font-medium text-[hsl(var(--nn-ink))]">
                  {l.title}
                </div>
                <div className="text-[11px] text-[hsl(var(--nn-ink-muted))]">
                  {l.authors} · {l.year}
                </div>
                {l.conclusion && (
                  <div className="mt-1 text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
                    {l.conclusion}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Derived Insights */}
      {data.insights && data.insights.length > 0 && (
        <>
          <CaseSectionHeader label="Derived insights" count={data.insights.length} />
          <ul className="flex flex-col gap-1.5">
            {data.insights.map((ins) => (
              <li
                key={ins.insightId}
                className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2"
              >
                <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
                  <BrainCircuit className="h-2.5 w-2.5" strokeWidth={1.8} />
                  <span>{ins.topic}</span>
                  {ins.confidence && (
                    <span className="ml-auto rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[9.5px] normal-case tracking-normal text-[hsl(var(--nn-ok))]">
                      {ins.confidence}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[12px] leading-[18px] text-[hsl(var(--nn-ink))]">
                  {ins.text}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* Small helpers for the extended Case view. */
function CaseSectionHeader({ label, count }) {
  return (
    <div className="flex items-center gap-2">
      <div className="nn-eyebrow">{label}</div>
      {typeof count === "number" && count > 0 && (
        <span className="font-mono text-[10px] text-[hsl(var(--nn-ink-faint))]">
          · {count}
        </span>
      )}
    </div>
  );
}

/* Case header cards get an icon by label so the data layer can stay
   free of React component references. Falls back to a neutral icon. */
function iconForCaseHeader(label) {
  const l = String(label || "").toLowerCase();
  if (l.includes("operate") || l.includes("date"))    return Calendar;
  if (l.includes("approach"))                          return MapPin;
  if (l.includes("outcome") || l.includes("complex")) return Activity;
  if (l.includes("attending") || l.includes("surgeon"))return Users;
  return Activity;
}

function ReflectionSource({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
        <Quote className="h-4 w-4 shrink-0 text-[hsl(var(--nn-amber))]" strokeWidth={1.8} />
        <p className="text-[13px] italic leading-[20px] text-[hsl(var(--nn-ink))]">
          {data.quote}
        </p>
      </div>
      <div>
        <div className="nn-eyebrow mb-1.5">Tags</div>
        <div className="flex flex-wrap gap-1.5">
          {data.tags.map((t) => (
            <span key={t} className="nn-chip">
              <Tag className="h-2.5 w-2.5" strokeWidth={2} />
              {t}
            </span>
          ))}
        </div>
      </div>
      {data.linkedCase && (
        <div className="text-[11.5px] text-[hsl(var(--nn-ink-muted))]">
          Linked case:{" "}
          <span className="font-mono text-[hsl(var(--nn-ink))]">
            {data.linkedCase}
          </span>
        </div>
      )}
    </div>
  );
}

function ImagingSource({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative h-[200px] w-full overflow-hidden rounded-xl border border-white/[0.08]"
        style={{
          background:
            "radial-gradient(ellipse at 40% 45%, rgba(240,240,255,0.55), rgba(80,90,120,0.65) 45%, rgba(20,26,40,1) 80%), #101623",
        }}
      >
        {/* Faux annotations */}
        <div
          className="absolute rounded-full border-2"
          style={{
            left: "38%", top: "42%", width: 42, height: 42,
            borderColor: "rgba(240, 165, 105, 0.75)",
            boxShadow: "0 0 0 3px rgba(240,165,105,0.15)",
          }}
        />
        <div
          className="absolute text-[10px] font-mono uppercase tracking-[0.14em]"
          style={{
            left: "48%", top: "34%",
            color: "hsl(var(--nn-amber))",
          }}
        >
          IAC · 2.6 cm
        </div>
        <div className="absolute bottom-2 left-2 rounded-md border border-white/10 bg-black/40 px-1.5 py-0.5 text-[10px] font-mono text-white/80">
          {data.modality}
        </div>
      </div>
      <div>
        <div className="nn-eyebrow mb-1.5">Annotated findings</div>
        <ul className="flex flex-col gap-1">
          {data.findings.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-2 rounded-lg border border-white/[0.05] bg-white/[0.02] px-2.5 py-1.5"
            >
              <span className="min-w-[70px] text-[11px] font-medium uppercase tracking-[0.10em] text-[hsl(var(--nn-blue))]">
                {f.at}
              </span>
              <span className="text-[12px] text-[hsl(var(--nn-ink))]">
                {f.note}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PaperSource({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] leading-[20px] text-[hsl(var(--nn-ink))]">
        {data.summary}
      </p>
      <div>
        <div className="nn-eyebrow mb-1.5">Key findings</div>
        <ul className="flex flex-col gap-1">
          {data.keyPoints.map((k, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-[12.5px] text-[hsl(var(--nn-ink-muted))]"
            >
              <Check
                className="mt-1 h-3 w-3 shrink-0 text-[hsl(var(--nn-blue))]"
                strokeWidth={2.2}
              />
              <span>{k}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MMSource({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] leading-[20px] text-[hsl(var(--nn-ink))]">
        {data.summary}
      </p>
      <div>
        <div className="nn-eyebrow mb-1.5">Discussion outcomes</div>
        <ul className="flex flex-col gap-1">
          {data.keyPoints.map((k, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-[12.5px] text-[hsl(var(--nn-ink-muted))]"
            >
              <AlertOctagon
                className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--nn-warn))]"
                strokeWidth={1.8}
              />
              <span>{k}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ------------------ Retrieval panel ------------------ */
function RetrievalPanel({ stages, activeIndex, activeSub }) {
  return (
    <div className="flex flex-col gap-1" data-testid="ni2-retrieval-panel">
      {stages.slice(0, activeIndex).map((s) => (
        <RetrievalDoneRow key={s.id} stage={s} />
      ))}
      {activeIndex < stages.length && (
        <RetrievalActiveRow
          key={`${stages[activeIndex].id}-${activeSub}`}
          stage={stages[activeIndex]}
          sub={activeSub}
        />
      )}
    </div>
  );
}

function RetrievalActiveRow({ stage, sub }) {
  const Icon = stage.Icon || Sparkles;
  return (
    <div
      className="nn-retrieval-active nn-soft-in"
      data-testid={`ni2-retrieval-active-${stage.id}`}
    >
      <span className="nn-retrieval-icon-btn">
        <Icon className="h-3 w-3" strokeWidth={1.8} />
      </span>
      {sub === "searching" ? (
        <>
          <span className="nn-shimmer-text">{stage.label}…</span>
          <span
            className="nn-pulse-dot ml-0.5"
            style={{ background: "hsl(var(--nn-blue))" }}
          />
        </>
      ) : (
        <>
          <span className="text-[hsl(var(--nn-ink))]">{stage.label}</span>
          <span className="nn-check">
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
          </span>
          <span className="nn-count">✓ {stage.detail}</span>
        </>
      )}
    </div>
  );
}

function RetrievalDoneRow({ stage }) {
  const Icon = stage.Icon || Sparkles;
  return (
    <div className="nn-retrieval-done">
      <span className="nn-retrieval-icon-sm">
        <Icon className="h-2.5 w-2.5" strokeWidth={1.8} />
      </span>
      <span>{stage.label}</span>
      <span className="nn-check">
        <Check className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
      <span className="nn-count">{stage.detail}</span>
    </div>
  );
}

/* ------------------ Synthesis panel ------------------ */
function SynthesisPanel({ summary, confidence }) {
  return (
    <div className="nn-synthesis" data-testid="ni2-synthesis-panel">
      <div className="flex items-center gap-2">
        <span
          className="grid h-6 w-6 place-items-center rounded-lg"
          style={{
            background:
              "linear-gradient(180deg, rgba(160,195,255,0.20), rgba(120,165,240,0.08))",
            border: "1px solid rgba(160,195,255,0.28)",
            color: "hsl(var(--nn-blue))",
          }}
        >
          <BrainCircuit className="h-3 w-3" strokeWidth={1.8} />
        </span>
        <span className="text-[12.5px] font-semibold tracking-tight text-[hsl(var(--nn-ink))]">
          Memory search complete
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 pl-8 sm:grid-cols-3">
        {summary.map((s, i) => (
          <div
            key={i}
            className="flex items-baseline gap-1.5 text-[11.5px] text-[hsl(var(--nn-ink-muted))]"
          >
            <span className="font-mono text-[12px] text-[hsl(var(--nn-ink))]">
              {s.count}
            </span>
            <span>{s.unit}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between pl-8">
        <div className="flex items-center gap-2">
          <span className="nn-confidence-label">Evidence confidence</span>
          <span className="nn-confidence-dot" />
          <span className="nn-confidence-value">{confidence}</span>
        </div>
        <span className="nn-shimmer-text text-[11.5px] font-medium tracking-tight">
          Synthesizing surgical reasoning…
        </span>
      </div>
    </div>
  );
}

/* ------------------ Streamed paragraph ------------------ */
function StreamedParagraph({ words }) {
  // Group tokens into paragraphs so breaks render as real <p>.
  const paragraphs = useMemo(() => {
    const paras = [[]];
    words.forEach((w) => {
      if (w.isBreak) paras.push([]);
      else paras[paras.length - 1].push(w);
    });
    return paras;
  }, [words]);

  return (
    <div className="text-[14px] leading-[22px] text-[hsl(var(--nn-ink))]">
      {paragraphs.map((para, pi) => (
        <p key={pi} className={pi > 0 ? "mt-2.5" : ""}>
          {para.map((w, i) => (
            <Fragment key={i}>
              <span
                className={`nn-stream-word ${w.cite ? "font-medium text-[hsl(var(--nn-blue))]" : ""}`}
              >
                {w.token}
              </span>
              {i < para.length - 1 ? " " : ""}
            </Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

/* ------------------ Confidence badge ------------------ */
function ConfidenceBadge({ value }) {
  return (
    <div className="nn-confidence" data-testid="ni2-confidence-badge">
      <ShieldCheck className="h-3 w-3 text-[hsl(var(--nn-ok))]" strokeWidth={1.8} />
      <span className="nn-confidence-label">Evidence confidence</span>
      <span className="nn-confidence-dot" />
      <span className="nn-confidence-value">{value}</span>
    </div>
  );
}

/* ------------------ Bubble actions ------------------ */
function BubbleActions() {
  return (
    <div className="mt-1.5 flex items-center gap-1 pl-1 text-[10.5px] text-[hsl(var(--nn-ink-muted))]">
      <button className="rounded p-1 transition-colors hover:bg-white/[0.04]" title="Copy">
        <Copy className="h-3 w-3" strokeWidth={1.8} />
      </button>
      <button className="rounded p-1 transition-colors hover:bg-white/[0.04]" title="Save">
        <Bookmark className="h-3 w-3" strokeWidth={1.8} />
      </button>
      <button
        className="rounded p-1 transition-colors hover:bg-white/[0.04]"
        title="Verify provenance"
      >
        <ShieldCheck className="h-3 w-3" strokeWidth={1.8} />
      </button>
    </div>
  );
}

function InlineCard({ card }) {
  const Icon = card.kind === "case" ? FolderOpen : card.kind === "paper" ? BookOpen : ImageIcon;
  const tone =
    card.kind === "case"
      ? "hsl(var(--nn-amber))"
      : card.kind === "paper"
        ? "hsl(var(--nn-blue))"
        : "rgba(255,255,255,0.7)";
  return (
    <button
      className="group flex items-start gap-2 rounded-xl border border-white/[0.08] bg-white/[0.03] p-2.5 text-left transition-colors hover:border-white/[0.15] hover:bg-white/[0.05]"
    >
      <div
        className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-white/[0.08] bg-white/[0.04]"
        style={{ color: tone }}
      >
        <Icon className="h-3 w-3" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-[11.5px] font-medium text-[hsl(var(--nn-ink))]">
          {card.title}
        </div>
        <div className="mt-0.5 truncate text-[10px] text-[hsl(var(--nn-ink-muted))]">
          {card.meta}
        </div>
      </div>
      <ChevronRight
        className="mt-1 h-3 w-3 shrink-0 text-[hsl(var(--nn-ink-faint))] opacity-0 transition-opacity group-hover:opacity-100"
        strokeWidth={1.8}
      />
    </button>
  );
}

/* ------------------ Composer ------------------ */
const ATTACH_OPTIONS = [
  { key: "case",       label: "Attach case",                Icon: FolderOpen },
  { key: "imaging",    label: "Attach imaging",             Icon: ImageIcon },
  { key: "reflection", label: "Attach reflection or note",  Icon: Lightbulb },
  { key: "literature", label: "Attach literature",          Icon: BookOpen },
];

function Composer({ onSend, onOpenFiles, filesActive }) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attached, setAttached] = useState(null);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentions, setMentions] = useState([]);
  const attachWrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!attachOpen) return;
    const onDown = (e) => {
      if (attachWrapRef.current && !attachWrapRef.current.contains(e.target)) {
        setAttachOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAttachOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [attachOpen]);

  const submit = () => {
    if (!value.trim()) return;
    onSend?.(value);
    setValue("");
    setAttached(null);
    setMentions([]);
    setMentionQuery(null);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !mentionQuery) {
      e.preventDefault();
      submit();
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setValue(v);
    const m = /@([\w-]{0,24})$/.exec(v);
    if (m) setMentionQuery(m[1]);
    else setMentionQuery(null);
  };

  const handlePickMention = (person) => {
    setValue((prev) => prev.replace(/@([\w-]{0,24})$/, `@${person.fullName} `));
    setMentions((prev) =>
      prev.find((p) => p.personId === person.personId)
        ? prev
        : [...prev, { personId: person.personId, name: person.fullName }]
    );
    setMentionQuery(null);
    inputRef.current?.focus();
  };

  return (
    <div
      className="nn-composer flex flex-col gap-2 rounded-2xl px-3 py-2.5"
      data-focused={focused || undefined}
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.14), 0 20px 60px -28px rgba(0,0,0,0.55)",
        backdropFilter: "blur(28px) saturate(160%)",
        WebkitBackdropFilter: "blur(28px) saturate(160%)",
        transition:
          "border-color 200ms ease, box-shadow 200ms ease, background 200ms ease",
      }}
    >
      <div
        className="flex items-center justify-between gap-2"
        style={{ paddingBottom: 2 }}
      >
        <Ni2ScopeSelector />
        <span className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
          Ni² · Retrieval-scoped answer
        </span>
      </div>

      {attached && (
        <div className="flex items-center gap-2 self-start rounded-lg border border-white/10 bg-white/[0.05] px-2 py-1 text-[11px] text-[hsl(var(--nn-ink-muted))]">
          <FileText className="h-3 w-3" strokeWidth={1.8} />
          <span>{attached.label}</span>
          <button
            onClick={() => setAttached(null)}
            className="ml-1 rounded p-0.5 text-[hsl(var(--nn-ink-faint))] hover:bg-white/10 hover:text-[hsl(var(--nn-ink))]"
            aria-label="Remove attachment"
          >
            ×
          </button>
        </div>
      )}

      {mentions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 self-start">
          {mentions.map((m) => (
            <span
              key={m.personId}
              className="inline-flex items-center gap-1 rounded-full border border-white/[0.10] bg-white/[0.06] px-2 py-0.5 text-[10.5px] text-[hsl(var(--nn-ink))]"
              data-testid={`ni2-composer-mention-${m.personId}`}
            >
              <AtSign className="h-2.5 w-2.5" strokeWidth={2} />
              {m.name}
              <span className="font-mono text-[9.5px] text-[hsl(var(--nn-ink-faint))]">
                {m.personId}
              </span>
              <button
                type="button"
                onClick={() =>
                  setMentions((prev) => prev.filter((x) => x.personId !== m.personId))
                }
                className="ml-0.5 rounded-full p-0.5 text-[hsl(var(--nn-ink-faint))] hover:bg-white/[0.08]"
                aria-label={`Remove ${m.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative flex items-center gap-2">
        {mentionQuery !== null && (
          <Ni2MentionMenu
            query={mentionQuery}
            onPick={handlePickMention}
            onClose={() => setMentionQuery(null)}
          />
        )}
        <div ref={attachWrapRef} className="relative shrink-0">
          <button
            className="nn-icon-btn"
            aria-label="Attach"
            aria-expanded={attachOpen}
            data-testid="ni2-composer-attach"
            onClick={() => setAttachOpen((v) => !v)}
          >
            <Plus className="h-4 w-4" strokeWidth={1.6} />
          </button>
          {attachOpen && (
            <div
              role="menu"
              data-testid="ni2-composer-attach-menu"
              className="nn-popover nn-soft-in absolute left-0 bottom-full z-30 mb-2 w-[220px] p-1.5"
            >
              <div className="px-2 pb-1 pt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--nn-ink-faint))]">
                Attach to conversation
              </div>
              {ATTACH_OPTIONS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  className="nn-popover-item"
                  data-testid={`ni2-composer-attach-${key}`}
                  onClick={() => {
                    setAttached({ key, label });
                    setAttachOpen(false);
                    inputRef.current?.focus();
                  }}
                >
                  <span
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-white/10 bg-white/[0.04]"
                    style={{
                      color:
                        key === "case"
                          ? "hsl(var(--nn-amber))"
                          : key === "literature"
                            ? "hsl(var(--nn-blue))"
                            : "rgba(255,255,255,0.72)",
                    }}
                  >
                    <Icon className="h-3 w-3" strokeWidth={1.8} />
                  </span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="flex-1 bg-transparent px-1 py-1.5 text-[13.5px] text-[hsl(var(--nn-ink))] placeholder:text-[hsl(var(--nn-ink-faint))] focus:outline-none"
          placeholder="Ask Ni² about a case, reflection, image or surgical question…"
          data-testid="ni2-composer-input"
        />
        {onOpenFiles && (
          <button
            type="button"
            className="nn-icon-btn shrink-0"
            aria-label="Open case files"
            title="Open case files"
            data-testid="ni2-composer-open-files"
            data-active={filesActive || undefined}
            onClick={onOpenFiles}
            style={
              filesActive
                ? {
                    background:
                      "linear-gradient(180deg, rgba(160,200,255,0.20), rgba(120,165,240,0.08))",
                    borderColor: "rgba(160,200,255,0.35)",
                    color: "hsl(var(--nn-ink))",
                  }
                : undefined
            }
          >
            <BookIcon className="h-4 w-4" strokeWidth={1.6} />
          </button>
        )}
        <button
          className="nn-icon-btn shrink-0"
          aria-label="Voice input"
          title="Voice"
          data-testid="ni2-composer-mic"
        >
          <Mic className="h-4 w-4" strokeWidth={1.6} />
        </button>
        <button
          onClick={submit}
          className="nn-icon-btn shrink-0"
          aria-label="Send"
          data-testid="ni2-composer-send"
          style={{
            background:
              "linear-gradient(180deg, rgba(160,200,255,0.28), rgba(120,165,240,0.14))",
            borderColor: "rgba(160,200,255,0.42)",
            color: "hsl(var(--nn-ink))",
          }}
        >
          <Send className="h-4 w-4" strokeWidth={1.6} />
        </button>
      </div>

      <div className="flex items-center justify-between px-1 text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors hover:bg-white/[0.05] hover:text-[hsl(var(--nn-ink-muted))]"
            title="Mention a case, surgeon or reflection"
          >
            <AtSign className="h-2.5 w-2.5" strokeWidth={2} />
            mention
          </button>
          <button className="rounded-md px-1.5 py-0.5 transition-colors hover:bg-white/[0.05] hover:text-[hsl(var(--nn-ink-muted))]">
            /case
          </button>
          <button className="rounded-md px-1.5 py-0.5 transition-colors hover:bg-white/[0.05] hover:text-[hsl(var(--nn-ink-muted))]">
            /reflect
          </button>
          <button className="rounded-md px-1.5 py-0.5 transition-colors hover:bg-white/[0.05] hover:text-[hsl(var(--nn-ink-muted))]">
            /prep
          </button>
        </div>
        <div className="flex items-center gap-1 font-mono">
          <kbd className="rounded border border-white/15 bg-white/[0.06] px-1">⌘</kbd>
          <kbd className="rounded border border-white/15 bg-white/[0.06] px-1">↩</kbd>
          <span className="ml-1">send</span>
        </div>
      </div>
    </div>
  );
}
